<template>
    <b-card title="Facturación" border-variant="primary" class="my-1 h-100">
        <b-overlay :show="loading" rounded="sm">

            <!-- Ya tiene factura viva -->
            <div v-if="status.invoiced">
                <b-alert variant="success" show class="mb-3">
                    <b-icon-check-circle-fill></b-icon-check-circle-fill>
                    Este ticket ya está facturado.
                </b-alert>
                <b-row cols-xl="3" cols-lg="3" cols-md="2" cols="1">
                    <b-col>
                        <p class="my-0">
                            Folio: {{ status.folio }}<br>
                            Receptor: {{ status.razon_social_receptor }}<br>
                            Total: $ {{ status.total }}
                        </p>
                    </b-col>
                    <b-col>
                        <p class="my-0">
                            Estatus: {{ status.status || "—" }}<br>
                            Fecha: {{ status.fecha_timbrado }}<br>
                            Proveedor: {{ status.provider_type }}
                        </p>
                    </b-col>
                    <b-col>
                        <p class="my-0 text-break">
                            <span v-if="status.cufe">CUFE: {{ status.cufe }}</span>
                            <span v-else-if="status.uuid">UUID: {{ status.uuid }}</span>
                        </p>
                    </b-col>
                </b-row>
                <b-row class="mt-3" v-if="status.provider_invoice_id">
                    <b-col>
                        <b-button size="sm" variant="primary" class="mr-2" :disabled="downloading"
                            @click="descargar('pdf')">DESCARGAR PDF</b-button>
                        <b-button size="sm" variant="primary" :disabled="downloading"
                            @click="descargar('xml')">DESCARGAR XML</b-button>
                    </b-col>
                </b-row>
            </div>

            <!-- La sucursal no puede facturar -->
            <b-alert v-else-if="!capabilities.configured" variant="warning" show class="mb-0">
                <b-icon-exclamation-triangle-fill></b-icon-exclamation-triangle-fill>
                {{ capabilities.reason || "Esta sucursal no tiene configurada la facturación." }}
                <span v-if="capabilities.country_name"> ({{ capabilities.country_name }})</span>
            </b-alert>

            <!-- El ticket no es facturable -->
            <b-alert v-else-if="motivoNoFacturable" variant="secondary" show class="mb-0">
                {{ motivoNoFacturable }}
            </b-alert>

            <!-- Captura de cliente y emisión -->
            <div v-else>
                <b-alert v-if="capabilities.sandbox" variant="info" show class="mb-3">
                    Esta sucursal está en modo pruebas ({{ capabilities.provider_type }}). La factura no tendrá validez
                    fiscal.
                </b-alert>

                <!-- México: el receptor es un cliente ya registrado con el proveedor -->
                <div v-if="capabilities.customer_mode === 'registry'">
                    <b-row>
                        <b-col md="8">
                            <b-form-group label="Cliente de facturación">
                                <b-input-group>
                                    <b-form-input debounce="500" v-model="busquedaCliente"
                                        placeholder="Buscar por RFC o razón social"></b-form-input>
                                </b-input-group>
                                <b-form-select class="mt-2" v-model="clienteSeleccionado" :options="opcionesClientes">
                                    <template #first>
                                        <b-form-select-option :value="null">-- Selecciona un cliente --
                                        </b-form-select-option>
                                    </template>
                                </b-form-select>
                            </b-form-group>
                        </b-col>
                        <b-col md="4">
                            <b-form-group label="Correo (opcional)">
                                <b-form-input v-model="form.customer_email" type="email"></b-form-input>
                            </b-form-group>
                        </b-col>
                    </b-row>
                </div>

                <!-- Colombia y demás: el receptor se captura aquí -->
                <div v-else>
                    <b-row>
                        <b-col md="3">
                            <b-form-group label="Tipo de persona">
                                <b-form-select v-model="form.customer_is_company" :options="opcionesTipoPersona">
                                </b-form-select>
                            </b-form-group>
                        </b-col>
                        <b-col md="3" v-if="capabilities.id_types && capabilities.id_types.length">
                            <b-form-group label="Tipo de identificación">
                                <b-form-select v-model="form.customer_id_type" :options="opcionesIdTypes">
                                </b-form-select>
                            </b-form-group>
                        </b-col>
                        <b-col md="3">
                            <b-form-group label="Identificación">
                                <b-form-input v-model="form.customer_tax_id" trim></b-form-input>
                            </b-form-group>
                        </b-col>
                        <b-col md="3">
                            <b-form-group :label="form.customer_is_company ? 'Razón social' : 'Nombre completo'">
                                <b-form-input v-model="form.customer_name" trim></b-form-input>
                            </b-form-group>
                        </b-col>
                    </b-row>
                    <b-row>
                        <b-col md="6">
                            <b-form-group label="Correo (opcional)">
                                <b-form-input v-model="form.customer_email" type="email" trim></b-form-input>
                            </b-form-group>
                        </b-col>
                        <b-col md="6">
                            <b-form-group label="Teléfono (opcional)">
                                <b-form-input v-model="form.customer_phone" trim></b-form-input>
                            </b-form-group>
                        </b-col>
                    </b-row>
                </div>

                <b-row>
                    <b-col>
                        <b-button variant="primary" :disabled="facturando || !datosCompletos" @click="facturar">
                            <b-spinner small v-if="facturando"></b-spinner>
                            FACTURAR
                        </b-button>
                        <small class="ml-3 text-muted" v-if="!datosCompletos">{{ faltante }}</small>
                    </b-col>
                </b-row>

                <b-alert variant="danger" class="mt-3 mb-0" :show="errores.length > 0">
                    <div v-for="(e, i) in errores" :key="i">{{ e }}</div>
                </b-alert>
            </div>

        </b-overlay>
    </b-card>
</template>
<script>
import axios from "axios";

export default {
    name: "FacturarTicket",
    props: {
        // El ticket tal cual lo devuelve /ticket/sid/{sid}
        ticket: { type: Object, required: true },
    },
    data() {
        return {
            loading: false,
            facturando: false,
            downloading: false,
            capabilities: {
                country_code: "",
                country_name: "",
                provider_type: "",
                configured: false,
                sandbox: false,
                customer_mode: "form",
                id_types: [],
                reason: "",
            },
            status: { invoiced: false },
            errores: [],
            form: {
                customer_name: "",
                customer_tax_id: "",
                customer_id_type: "",
                customer_is_company: false,
                customer_email: "",
                customer_phone: "",
            },
            busquedaCliente: "",
            clientesFacturacion: [],
            clienteSeleccionado: null,
            opcionesTipoPersona: [
                { value: false, text: "Persona" },
                { value: true, text: "Empresa" },
            ],
        };
    },
    computed: {
        sucursalId() {
            return this.ticket ? this.ticket.sucursal_id : 0;
        },
        ticketSid() {
            return this.ticket ? this.ticket.sid : 0;
        },
        opcionesIdTypes() {
            return (this.capabilities.id_types || []).map((t) => ({
                value: t.code,
                text: t.code + " - " + t.name,
            }));
        },
        opcionesClientes() {
            return this.clientesFacturacion.map((c) => ({
                value: c.uid,
                text: (c.rfc || "") + " - " + (c.razon_social || c.nombre || ""),
            }));
        },
        // Un ticket cancelado o sin pagar lo rechaza el backend, así que se dice
        // aquí en vez de dejar que el usuario descubra el error al apretar.
        motivoNoFacturable() {
            if (!this.ticket || !this.ticket.sid) return "";
            if (this.ticket.canceled) return "La cuenta está cancelada, no se puede facturar.";
            if (!this.ticket.paid) return "La cuenta debe estar pagada antes de facturar.";
            return "";
        },
        datosCompletos() {
            if (this.capabilities.customer_mode === "registry") {
                return !!this.clienteSeleccionado;
            }
            return this.form.customer_tax_id.length > 0 && this.form.customer_name.length > 0;
        },
        faltante() {
            if (this.capabilities.customer_mode === "registry") {
                return "Selecciona el cliente de facturación";
            }
            return "Captura identificación y nombre";
        },
    },
    watch: {
        ticketSid: {
            immediate: true,
            handler(sid) {
                if (sid > 0) this.cargar();
            },
        },
        busquedaCliente() {
            if (this.capabilities.customer_mode === "registry") this.buscarClientes();
        },
    },
    methods: {
        cargar() {
            this.errores = [];
            this.status = { invoiced: false };
            this.loading = true;
            let that = this;
            Promise.all([this.cargarCapabilities(), this.cargarStatus()])
                .then(function () {
                    that.loading = false;
                    if (!that.status.invoiced && that.capabilities.configured) {
                        that.prepararFormulario();
                    }
                })
                .catch(function () {
                    that.loading = false;
                });
        },
        cargarCapabilities() {
            let that = this;
            return axios
                .get("/compliance/capabilities", { params: { sucursal_id: this.sucursalId } })
                .then(function (response) {
                    that.capabilities = response.data;
                    if (!that.form.customer_id_type && that.capabilities.id_types && that.capabilities.id_types.length) {
                        that.form.customer_id_type = that.capabilities.id_types[0].code;
                    }
                })
                .catch(function () {
                    // Sin configuración legible el botón no se muestra; el motivo
                    // queda en el alert en vez de en un toast que el usuario pierde.
                    that.capabilities = {
                        configured: false,
                        customer_mode: "form",
                        id_types: [],
                        reason: "No se pudo consultar la configuración de facturación de la sucursal.",
                    };
                });
        },
        cargarStatus() {
            let that = this;
            return axios
                .get("/compliance/ticket/" + this.ticketSid + "/invoice")
                .then(function (response) {
                    that.status = response.data;
                })
                .catch(function () {
                    that.status = { invoiced: false };
                });
        },
        prepararFormulario() {
            if (this.capabilities.customer_mode === "registry") {
                this.buscarClientes();
                return;
            }
            // El ticket no guarda identificación fiscal, así que solo se puede
            // precargar el contacto del cliente ligado, si lo hay y si el usuario
            // tiene permiso de leerlo.
            if (!this.ticket.customer_id) return;
            let that = this;
            axios
                .get("/customers/" + this.ticket.customer_id)
                .then(function (response) {
                    let c = response.data || {};
                    if (!that.form.customer_name && c.name) that.form.customer_name = c.name;
                    if (!that.form.customer_email && c.email) that.form.customer_email = c.email;
                    if (!that.form.customer_phone && c.phone) that.form.customer_phone = c.phone;
                })
                .catch(function () {
                    // Sin permiso de clientes o sin cliente: se captura a mano.
                });
        },
        buscarClientes() {
            let that = this;
            let extra = this.busquedaCliente.length > 0 ? "?search=" + encodeURIComponent(this.busquedaCliente) : "";
            axios
                .get("/company/1/sucursal/" + this.sucursalId + "/facturacion/clientes" + extra)
                .then(function (response) {
                    that.clientesFacturacion = response.data || [];
                })
                .catch(function () {
                    that.clientesFacturacion = [];
                });
        },
        facturar() {
            this.errores = [];
            this.facturando = true;
            let that = this;

            let payload = {
                ticket_id: this.ticket.id,
                customer_email: this.form.customer_email,
            };

            if (this.capabilities.customer_mode === "registry") {
                // El CFDI referencia al receptor por el UID del cliente ya dado de
                // alta con el proveedor, no por un RFC capturado.
                let cliente = this.clientesFacturacion.find((c) => c.uid === this.clienteSeleccionado) || {};
                payload.customer_tax_id = this.clienteSeleccionado;
                payload.customer_name = cliente.razon_social || cliente.nombre || "";
                if (!payload.customer_email) payload.customer_email = cliente.email || "";
            } else {
                payload.customer_tax_id = this.form.customer_tax_id;
                payload.customer_name = this.form.customer_name;
                payload.customer_id_type = this.form.customer_id_type;
                payload.customer_is_company = this.form.customer_is_company;
                payload.customer_phone = this.form.customer_phone;
            }

            axios
                .post("/compliance/invoice", payload, { params: { sucursal_id: this.sucursalId } })
                .then(function (response) {
                    that.facturando = false;
                    that.makeToast("success", "Factura generada", "Folio " + (response.data.invoice_id || ""));
                    that.$emit("invoiced", that.ticketSid);
                    that.cargarStatus();
                })
                .catch(function (r) {
                    that.facturando = false;
                    that.errores = that.leerErrores(r);
                });
        },
        // El backend responde con {errors:[...]} cuando el proveedor rechaza, y con
        // {error, details} cuando falla antes de llegar a él. Los dos importan.
        leerErrores(r) {
            let data = r && r.response ? r.response.data : null;
            if (!data) return ["No se pudo contactar al servidor."];
            if (Array.isArray(data.errors) && data.errors.length) return data.errors;
            if (data.error && data.details) return [data.error + ": " + data.details];
            if (data.error) return [data.error];
            if (typeof data === "string") return [data];
            return ["La factura no se pudo generar."];
        },
        descargar(tipo) {
            this.downloading = true;
            let that = this;
            axios
                .get("/compliance/invoice/" + this.status.provider_invoice_id + "/" + tipo, { responseType: "blob" })
                .then(function (response) {
                    let url = window.URL.createObjectURL(new Blob([response.data]));
                    let link = document.createElement("a");
                    link.href = url;
                    link.setAttribute("download", "factura_" + that.status.folio + "." + tipo);
                    document.body.appendChild(link);
                    link.click();
                    link.remove();
                    window.URL.revokeObjectURL(url);
                    that.downloading = false;
                })
                .catch(function () {
                    that.downloading = false;
                    that.makeToast("danger", "Error", "No se pudo descargar el " + tipo.toUpperCase());
                });
        },
        makeToast(variant = null, title = "", text = "") {
            this.$bvToast.toast(text, {
                title: title,
                variant: variant,
                solid: true,
                appendToast: true,
            });
        },
    },
};
</script>
